import { CalendarDate } from '@internationalized/date';
import { JSONObject } from '@lucidtech/las-sdk-browser';
import { memo, useCallback, useMemo, useState } from 'react';
import { DateRange, Focusable } from 'react-aria-components';

import { merge } from '@/components';
import {
  BasicTooltip,
  Button,
  DateRangePicker,
  Flex,
  Heading,
  ProgressBar,
  Table,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@/components/core';
import { ArrowIcon, DateIcon, InsightsIcon } from '@/components/icons';
import { FieldLogo } from '@/components/logos';
import { BetaBadge } from '@/components/ui';
import { useGetAgentFromPath } from '@/hooks';
import { toAgentStatisticId, toModelId, useGetAgentStatistics, useGetModel } from '@/hooks/api';
import { DivProps, findModelId, isNumber, OmitChildren, toNumber } from '@/utils';

const DEFAULT_NUM_DAYS = 30;

const useValueVariant = (value: number, lowerIsBetter?: boolean) => {
  return useMemo(() => {
    if (lowerIsBetter) {
      return value <= 0.2 ? 'success' : value <= 0.7 ? 'secondary' : 'warning';
    } else {
      return value >= 0.8 ? 'success' : value >= 0.3 ? 'secondary' : 'warning';
    }
  }, [lowerIsBetter, value]);
};

const useChangeVariant = (change: number, lowerIsBetter?: boolean) => {
  return useMemo(() => {
    if (change === 0) return 'secondary';
    if (lowerIsBetter) {
      return change < 0 ? 'success' : 'warning';
    } else {
      return change > 0 ? 'success' : 'warning';
    }
  }, [change, lowerIsBetter]);
};

type MetricCardProps = {
  heading: string;
  value: number;
  change: number;
  datapoints?: number;
  lowerIsBetter?: boolean;
  tooltip?: string;
  isPercentage?: boolean;
};

const IMetricCard = ({ heading, value, change, datapoints, lowerIsBetter, tooltip, isPercentage }: MetricCardProps) => {
  const changeVariant = useChangeVariant(change, lowerIsBetter);

  const valueText = isPercentage ? `${(value * 100).toFixed(1)}%` : value.toString();
  const changeText = isPercentage ? `${(change * 100).toFixed(1)}%` : change.toString();

  return (
    <Flex col variant="secondary" className="h-fit gap-4 rounded-lg p-4 shadow-xs">
      <div className="flex items-center gap-2">
        <Heading className="font-medium text-gray-600" size="md">
          {heading}
        </Heading>
        {tooltip ? (
          <BasicTooltip placement="top">
            <Text>{tooltip}</Text>
          </BasicTooltip>
        ) : null}
      </div>
      <Flex className="items-center gap-2" variant="plain">
        <Text className="font-semibold text-gray-900" size="xl">
          {valueText}
        </Text>
        {datapoints ? <Text className="text-xs text-gray-500"># {datapoints}</Text> : null}
      </Flex>
      <Flex className="items-center gap-2" variant="plain">
        {Math.abs(change) < 0.01 ? (
          <Text>Unchanged from last period</Text>
        ) : (
          <>
            <Flex>
              <ArrowIcon variant={changeVariant} rotation={change > 0 ? 'up' : 'down'} />
              <Text variant={changeVariant} className="font-medium">
                {changeText}
              </Text>
            </Flex>
            <Text>last period</Text>
          </>
        )}
      </Flex>
    </Flex>
  );
};

const MetricCard = memo(IMetricCard) as typeof IMetricCard;

type BarCellProps = OmitChildren<DivProps> & {
  value: number;
  lowerIsBetter?: boolean;
  tooltip?: string;
};

const IBarCell = ({ value, lowerIsBetter, tooltip, className, ...rest }: BarCellProps) => {
  const valueVariant = useValueVariant(value, lowerIsBetter);

  return (
    <TooltipTrigger>
      <Tooltip className="w-48" variant="primary">
        <Text slot="description">{tooltip}</Text>
      </Tooltip>

      <Focusable>
        <Flex
          className={merge('w-fit cursor-default items-center gap-1 rounded-sm px-1.5 shadow-xs', className)}
          variant="secondary"
          role="button"
          {...rest}
        >
          <ProgressBar
            className="w-16"
            variant={valueVariant}
            value={value * 100}
            maxValue={100}
            barStyle="striped-tight"
            props={{ barBackground: { className: 'h-1.5' }, barOverlay: { className: 'h-1.5' } }}
          />
          <Text size="xs">{(value * 100).toFixed(1)}%</Text>
        </Flex>
      </Focusable>
    </TooltipTrigger>
  );
};

const BarCell = memo(IBarCell) as typeof IBarCell;

type BadgeCellProps = OmitChildren<DivProps> & {
  value: number;
  lowerIsBetter?: boolean;
  tooltip?: string;
};

const IBadgeCell = ({ value, lowerIsBetter, tooltip, className, ...rest }: BadgeCellProps) => {
  const valueVariant = useValueVariant(value, lowerIsBetter);

  return (
    <TooltipTrigger>
      <Tooltip className="w-48" variant="primary">
        <Text slot="description">{tooltip}</Text>
      </Tooltip>

      <Focusable>
        <Flex
          className={merge('w-fit cursor-default items-center gap-1 rounded-sm px-1.5 shadow-xs', className)}
          variant="secondary"
          role="button"
          {...rest}
        >
          <div
            className={merge(
              'bg-primary-500 h-1 w-1 rounded-full',
              valueVariant === 'success' && 'bg-success-500',
              valueVariant === 'warning' && 'bg-warning-500',
              valueVariant === 'secondary' && 'bg-gray-500'
            )}
          />
          <Text size="xs">{(value * 100).toFixed(1)}%</Text>
        </Flex>
      </Focusable>
    </TooltipTrigger>
  );
};

const BadgeCell = memo(IBadgeCell) as typeof IBadgeCell;

const createDateRange = (days: number, anchor?: Date) => {
  const now = anchor ? anchor : new Date();
  const end = new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
  now.setDate(now.getDate() - days);
  const start = new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
  return { start, end };
};

export type InsightsProps = object;

// eslint-disable-next-line no-empty-pattern
const IInsights = ({}: InsightsProps) => {
  const { data: agent } = useGetAgentFromPath();
  const { data: model } = useGetModel(toModelId(findModelId(agent)));

  const [dateSelection, setDateSelection] = useState<string | number>(DEFAULT_NUM_DAYS);
  const [dateRange, setDateRange] = useState<DateRange | null>(createDateRange(DEFAULT_NUM_DAYS));

  const setDateRangeOption = useCallback((days: number) => {
    setDateSelection(days);
    setDateRange(createDateRange(days));
  }, []);

  const setCustomDateRangeOption = useCallback((range: DateRange | null) => {
    if (range) {
      setDateSelection('custom');
      setDateRange(range);
    }
  }, []);

  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);

  const dateRangeToDates = useCallback(
    (dateRange: DateRange) => {
      const now = new Date();
      const after = dateRange.start.toDate(timezone);
      after.setHours(now.getHours());
      after.setMinutes(now.getMinutes());
      after.setSeconds(now.getSeconds());
      const before = dateRange.end.toDate(timezone);
      before.setHours(now.getHours());
      before.setMinutes(now.getMinutes());
      before.setSeconds(now.getSeconds());
      return { after, before };
    },
    [timezone]
  );

  const statisticsOptions = useMemo(() => {
    if (!agent || !dateRange) return;
    const { before, after } = dateRangeToDates(dateRange);
    return toAgentStatisticId(agent?.agentId, before, after);
  }, [agent, dateRange, dateRangeToDates]);

  const previousStatisticsOptions = useMemo(() => {
    if (!agent || !dateRange) return;
    const previousDateRange = createDateRange(
      toNumber(dateSelection, DEFAULT_NUM_DAYS),
      dateRange.start.toDate(timezone)
    );
    const { before, after } = dateRangeToDates(previousDateRange);
    return toAgentStatisticId(agent?.agentId, before, after);
  }, [agent, dateRange, dateRangeToDates, dateSelection, timezone]);

  const { data: statistics } = useGetAgentStatistics(statisticsOptions);
  const { data: previousStatistics } = useGetAgentStatistics(previousStatisticsOptions);

  const getData = useCallback((data?: JSONObject | null) => {
    const numDocuments = isNumber(data?.documents) ? data.documents : 0;
    const numDocumentsAutomated = isNumber(data?.documentsAutomated) ? data.documentsAutomated : 0;
    const numValues = isNumber(data?.values) ? data.values : 0;
    const numValuesAutomated = isNumber(data?.valuesAutomated) ? data.valuesAutomated : 0;
    const numValuesEdited = isNumber(data?.valuesEdited) ? data.valuesEdited : 0;
    const numValuesDeleted = isNumber(data?.valuesDeleted) ? data.valuesDeleted : 0;

    return {
      numDocuments,
      numDocumentsAutomated,
      numValues,
      numValuesAutomated,
      numValuesEdited: numValuesEdited + numValuesDeleted,
      numValuesFlagged: numValues - numValuesAutomated,
    };
  }, []);

  const createMetric = useCallback((num: number, total: number, previousNum: number, previousTotal: number) => {
    const value = total === 0 ? 0 : num / total;
    const previousValue = previousTotal === 0 ? 0 : previousNum / previousTotal;

    return {
      value,
      change: value - previousValue,
      datapoints: num,
    };
  }, []);

  const { runs, automation, fullAutomation, flagged, edits } = useMemo(() => {
    const data = getData(statistics?.statistics.global);
    const previousData = getData(previousStatistics?.statistics.global);

    const runs = {
      value: data.numDocuments,
      change: data.numDocuments - previousData.numDocuments,
    };

    return {
      runs,
      automation: createMetric(
        data.numValuesAutomated,
        data.numValues,
        previousData.numValuesAutomated,
        previousData.numValues
      ),
      fullAutomation: createMetric(
        data.numDocumentsAutomated,
        data.numDocuments,
        previousData.numDocumentsAutomated,
        previousData.numDocuments
      ),
      flagged: createMetric(
        data.numValuesFlagged,
        data.numValues,
        previousData.numValuesFlagged,
        previousData.numValues
      ),
      edits: createMetric(data.numValuesEdited, data.numValues, previousData.numValuesEdited, previousData.numValues),
    };
  }, [getData, statistics?.statistics.global, previousStatistics?.statistics.global, createMetric]);

  const rows = useMemo(() => {
    const data = [];

    for (const [fieldId, config] of Object.entries(model?.fieldConfig ?? {})) {
      if (config.type === 'table') {
        for (const [subFieldId, subConfig] of Object.entries(config.fields ?? {})) {
          const id = `${fieldId}/${subFieldId}`;
          const subFieldData = getData(statistics?.statistics.fields[id]);
          const previousSubFieldData = getData(previousStatistics?.statistics.fields[id]);

          data.push({
            id,
            automation: createMetric(
              subFieldData.numValuesAutomated,
              subFieldData.numValues,
              previousSubFieldData.numValuesAutomated,
              previousSubFieldData.numValues
            ),
            flagged: createMetric(
              subFieldData.numValuesFlagged,
              subFieldData.numValues,
              previousSubFieldData.numValuesFlagged,
              previousSubFieldData.numValues
            ),
            edits: createMetric(
              subFieldData.numValuesEdited,
              subFieldData.numValues,
              previousSubFieldData.numValuesEdited,
              previousSubFieldData.numValues
            ),
            ...subConfig,
            name: `${config.name} / ${subConfig.name}`,
          });
        }
      } else {
        const fieldData = getData(statistics?.statistics.fields[fieldId]);
        const previousFieldData = getData(previousStatistics?.statistics.fields[fieldId]);

        data.push({
          id: fieldId,
          automation: createMetric(
            fieldData.numValuesAutomated,
            fieldData.numValues,
            previousFieldData.numValuesAutomated,
            previousFieldData.numValues
          ),
          flagged: createMetric(
            fieldData.numValuesFlagged,
            fieldData.numValues,
            previousFieldData.numValuesFlagged,
            previousFieldData.numValues
          ),
          edits: createMetric(
            fieldData.numValuesEdited,
            fieldData.numValues,
            previousFieldData.numValuesEdited,
            previousFieldData.numValues
          ),
          ...config,
        });
      }
    }
    return data;
  }, [model?.fieldConfig, getData, statistics?.statistics.fields, previousStatistics?.statistics.fields, createMetric]);

  const ColumnHeader = ({ header }: { header: string }) => {
    return (
      <div className="px-4 py-2">
        <Text className="!text-xs font-medium text-gray-500">{header}</Text>
      </div>
    );
  };

  const columns = useMemo(() => {
    return [
      {
        header: () => <ColumnHeader header="Field breakdown" />,
        accessorKey: 'id',
        cell: ({ row }) => {
          const [name, subName] = row.original.name.split(' / ');

          return (
            <Flex variant="plain" className="m-4 items-center gap-2">
              <FieldLogo field={row.original} size="md" />
              {subName ? (
                <>
                  <Text className="text-gray-400">{name} /</Text>
                  <Text>{subName}</Text>
                </>
              ) : (
                <Text>{name}</Text>
              )}
            </Flex>
          );
        },
        size: 400,
      },
      {
        header: () => <ColumnHeader header="Automation" />,
        accessorKey: 'automation',
        cell: ({ row }) => {
          return (
            <div className="m-4">
              <BarCell
                value={row.original.automation.value}
                tooltip={`${row.original.automation.datapoints} ${row.original.name} values (${(row.original.automation.value * 100).toFixed(1)}%) were automatically validated without requiring human review.`}
              />
            </div>
          );
        },
      },
      {
        header: () => <ColumnHeader header="Flagged for review" />,
        accessorKey: 'flagged',
        cell: ({ row }) => {
          return (
            <div className="m-4">
              <BadgeCell
                value={row.original.flagged.value}
                tooltip={`${row.original.flagged.datapoints} ${row.original.name} values (${(row.original.flagged.value * 100).toFixed(1)}%) were flagged for human review because the agent's confidence was below the set threshold or validation checks failed.`}
                lowerIsBetter
              />
            </div>
          );
        },
      },
      {
        header: () => <ColumnHeader header="Corrected by human" />,
        accessorKey: 'edits',
        cell: ({ row }) => {
          return (
            <div className="m-4">
              <BadgeCell
                value={row.original.edits.value}
                tooltip={`${row.original.edits.datapoints} ${row.original.name} values (${(row.original.edits.value * 100).toFixed(1)}%) were corrected by a human during review.`}
                lowerIsBetter
              />
            </div>
          );
        },
      },
    ];
  }, []);

  return (
    <div className="flex min-h-0 w-full flex-col gap-4 bg-gray-50">
      <div className="py-md flex h-12 w-full items-center gap-2 border-b-1 border-gray-200 bg-white px-12">
        <InsightsIcon />
        <Heading size="md">Insights</Heading>
        <BetaBadge />
      </div>

      <div className="mx-16 flex h-[calc(100vh-(--spacing(24)))] flex-col gap-4">
        <div className="flex items-center gap-4">
          <Flex variant="secondary" className="rounded-lg shadow-xs">
            <div
              className={merge(
                'items-center rounded-l-lg hover:bg-gray-100',
                dateSelection === 'custom' && 'bg-gray-100'
              )}
            >
              <Flex variant="plain" className="items-center p-1 pl-3">
                <DateIcon />
                <DateRangePicker
                  label="Select date range"
                  dateRange={dateRange}
                  onDateRangeChange={setCustomDateRangeOption}
                />
              </Flex>
            </div>
            {[7, 30, 90].map((days, i) => (
              <div
                key={i}
                className={merge(
                  'flex items-center border-l border-gray-200',
                  i == 2 && 'rounded-r-lg pr-1',
                  days === dateSelection && 'bg-gray-100'
                )}
              >
                <Button onPress={() => setDateRangeOption(days)} variant="plain">
                  <Text className="!text-gray-700">{days} days</Text>
                </Button>
              </div>
            ))}
          </Flex>
        </div>

        <div className="grid grid-cols-5 gap-4">
          <MetricCard
            heading="Agent runs"
            tooltip="The number of times the agent has been executed, including both successful and failed runs."
            {...runs}
          />
          <MetricCard
            heading="Automation"
            tooltip="The amount of values automatically validated without requiring human review."
            isPercentage
            {...automation}
          />

          <MetricCard
            heading="Flagged for review"
            tooltip="The amount of values flagged for human review because the agent's confidence was below the set threshold or validation checks failed."
            isPercentage
            lowerIsBetter
            {...flagged}
          />
          <MetricCard
            heading="Corrected by human"
            tooltip="The amount of values where a human made changes during review, indicating the agent's output needed correction."
            isPercentage
            lowerIsBetter
            {...edits}
          />

          <MetricCard
            heading="Straight through"
            tooltip="The amount of documents automatically validated without requiring human review."
            isPercentage
            {...fullAutomation}
          />
        </div>

        <Flex col variant="secondary" className="rounded-lg shadow-xs">
          <div className="overflow-auto">
            <Table
              className="w-full overflow-auto"
              props={{
                row: { className: 'border-b border-gray-200' },
                header: { className: 'rounded-lg bg-gray-50 border-b border-gray-200' },
              }}
              data={rows}
              columns={columns}
              hideDragHandle
              hideRowOptions
            />
          </div>
        </Flex>
      </div>
    </div>
  );
};

export const Insights = memo(IInsights) as typeof IInsights;
